<template>
  <div class="riddles-game">
    <!-- 游戏头部 -->
    <div class="riddles-header">
      <div class="header-left">
        <h2>{{ t('games.riddles.title') }}</h2>
        <p class="difficulty-label" v-if="gameState !== 'selecting'">{{ difficultyLabel }}</p>
      </div>
      <div class="header-right">
        <div class="game-stats">
          <div class="stat-item">
            <span class="stat-label">{{ t('game.hud.score') }}</span>
            <span class="stat-value">{{ score }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">{{ t('game.hud.timer') }}</span>
            <span class="stat-value">{{ formatTime(elapsedTime) }}</span>
          </div>
        </div>
        <button class="btn-secondary btn-exit" @click="goHome">{{ t('buttons.backHome') }}</button>
      </div>
    </div>

    <!-- 游戏内容 -->
    <div class="riddles-main">
      <!-- 难度选择 -->
      <div v-if="gameState === 'selecting'" class="difficulty-selection">
        <h3>{{ t('games.riddles.selectDifficulty') }}</h3>
        <div class="difficulty-options">
          <button
            v-for="difficulty in difficulties"
            :key="difficulty"
            class="difficulty-btn"
            @click="startGame(difficulty)"
          >
            <span class="difficulty-name">{{ getDifficultyName(difficulty) }}</span>
            <span class="difficulty-desc">{{ getDifficultyDesc(difficulty) }}</span>
          </button>
        </div>
      </div>

      <!-- 游戏进行中 -->
      <div v-else-if="gameState === 'playing'" class="game-content">
        <!-- 进度条 -->
        <div class="progress-section">
          <div class="progress-header">
            <span class="question-counter">{{ currentQuestionIndex + 1 }}/{{ totalQuestions }}</span>
            <div class="progress-bar-container">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
              </div>
            </div>
          </div>
        </div>

        <!-- 问题区域 -->
        <div class="question-section">
          <div class="question-card">
            <h3 class="question-text">{{ currentQuestion[currentLang].question }}</h3>
            <div class="options-grid">
              <button
                v-for="(option, index) in currentQuestion[currentLang].options"
                :key="index"
                class="option-btn"
                :class="{
                  'option-selected': selectedAnswer === index,
                  'option-correct': showResult && index === currentQuestion[currentLang].correct,
                  'option-incorrect': showResult && selectedAnswer === index && index !== currentQuestion[currentLang].correct
                }"
                :disabled="showResult"
                @click="selectAnswer(index)"
              >
                <span class="option-letter">{{ String.fromCharCode(65 + index) }}</span>
                <span class="option-text">{{ option }}</span>
              </button>
            </div>
          </div>

          <!-- 解释 -->
          <div v-if="showResult" class="explanation-section">
            <div :class="['explanation-card', isCorrect ? 'correct' : 'incorrect']">
              <h4>{{ isCorrect ? '✓ 正确' : '✗ 错误' }}</h4>
              <p>{{ currentQuestion[currentLang].explanation }}</p>
              <button class="btn-primary btn-next" @click="nextQuestion">
                {{ currentQuestionIndex < totalQuestions - 1 ? t('buttons.next') : t('buttons.finish') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 游戏结束 -->
      <div v-else-if="gameState === 'finished'" class="game-result">
        <div class="result-card">
          <div :class="['result-icon', finalScore >= totalQuestions * 0.7 ? 'success' : 'normal']">
            {{ finalScore >= totalQuestions * 0.7 ? '🎉' : '👏' }}
          </div>
          <h3 class="result-title">
            {{ finalScore >= totalQuestions * 0.7 ? t('games.riddles.congratulations') : t('games.riddles.goodJob') }}
          </h3>
          <div class="result-stats">
            <div class="stat-row">
              <span class="stat-label">{{ t('games.riddles.correctAnswers') }}</span>
              <span class="stat-value">{{ finalScore }}/{{ totalQuestions }}</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">{{ t('games.riddles.accuracy') }}</span>
              <span class="stat-value">{{ Math.round((finalScore / totalQuestions) * 100) }}%</span>
            </div>
            <div class="stat-row">
              <span class="stat-label">{{ t('game.hud.timer') }}</span>
              <span class="stat-value">{{ formatTime(elapsedTime) }}</span>
            </div>
          </div>
          <div class="result-actions">
            <button class="btn-primary" @click="() => { gameState = 'selecting'; }">
              {{ t('buttons.playAgain') }}
            </button>
            <button class="btn-secondary" @click="goHome">
              {{ t('buttons.backHome') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from '@/utils/i18n'
import { riddleQuestions, getQuestionsByDifficulty } from './riddleQuestions'

const { t, currentLang } = useI18n()

// 游戏状态
const gameState = ref('selecting') // selecting, playing, finished
const currentDifficulty = ref('easy')
const currentQuestionIndex = ref(0)
const selectedAnswer = ref(null)
const showResult = ref(false)
const score = ref(0)
const elapsedTime = ref(0)
const questions = ref([])

// 计时器
let timerInterval = null

// 难度配置
const difficulties = ['easy', 'medium', 'hard']

const difficultyConfig = computed(() => ({
  easy: { label: t('games.riddles.easy'), desc: t('games.riddles.easyDesc'), count: 5 },
  medium: { label: t('games.riddles.medium'), desc: t('games.riddles.mediumDesc'), count: 10 },
  hard: { label: t('games.riddles.hard'), desc: t('games.riddles.hardDesc'), count: 15 }
}))

// 计算属性
const difficultyLabel = computed(() => {
  return difficultyConfig.value[currentDifficulty.value]?.label || ''
})

const currentQuestion = computed(() => {
  return questions.value[currentQuestionIndex.value] || null
})

const totalQuestions = computed(() => {
  return questions.value.length
})

const progressPercentage = computed(() => {
  if (totalQuestions.value === 0) return 0
  return ((currentQuestionIndex.value + 1) / totalQuestions.value) * 100
})

const isCorrect = computed(() => {
  if (!currentQuestion.value) return false
  return selectedAnswer.value === currentQuestion.value[currentLang.value].correct
})

const finalScore = computed(() => {
  return score.value
})

// 开始游戏
const startGame = (difficulty) => {
  try {
    currentDifficulty.value = difficulty
    const diffConfig = difficultyConfig.value[difficulty]

    console.log('Starting riddles game with difficulty:', difficulty)

    const availableQuestions = getQuestionsByDifficulty(difficulty)
    console.log('Available questions:', availableQuestions?.length || 0)

    if (!availableQuestions || availableQuestions.length === 0) {
      console.error('No questions available for difficulty:', difficulty)
      alert('Failed to load questions. Please try again.')
      return
    }

    // 随机选择问题
    questions.value = []
    const indices = new Set()
    const questionCount = Math.min(diffConfig.count, availableQuestions.length)

    while (questions.value.length < questionCount) {
      const randomIndex = Math.floor(Math.random() * availableQuestions.length)
      if (!indices.has(randomIndex)) {
        indices.add(randomIndex)
        questions.value.push(availableQuestions[randomIndex])
      }
    }

    console.log('Selected questions:', questions.value.length)

    currentQuestionIndex.value = 0
    selectedAnswer.value = null
    showResult.value = false
    score.value = 0
    elapsedTime.value = 0
    gameState.value = 'playing'

    // 启动计时器
    if (timerInterval) clearInterval(timerInterval)
    timerInterval = setInterval(() => {
      elapsedTime.value++
    }, 1000)
  } catch (error) {
    console.error('Error starting riddles game:', error)
    alert('Error starting game: ' + error.message)
  }
}

// 选择答案
const selectAnswer = (index) => {
  if (showResult.value) return

  selectedAnswer.value = index

  // 检查答案
  if (isCorrect.value) {
    score.value++
  }

  showResult.value = true
}

// 下一个问题
const nextQuestion = () => {
  if (currentQuestionIndex.value < totalQuestions.value - 1) {
    currentQuestionIndex.value++
    selectedAnswer.value = null
    showResult.value = false
  } else {
    // 游戏结束
    gameState.value = 'finished'
    if (timerInterval) clearInterval(timerInterval)
  }
}

// 返回主页
const goHome = () => {
  if (timerInterval) clearInterval(timerInterval)

  // 如果在游戏中（playing/finished），回到难度选择
  if (gameState.value !== 'selecting') {
    gameState.value = 'selecting'
  } else {
    // 如果在难度选择页，回到首页
    window.location.hash = '#/'
  }
}

// 辅助函数
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${String(secs).padStart(2, '0')}`
}

const getDifficultyName = (difficulty) => {
  return difficultyConfig.value[difficulty]?.label || difficulty
}

const getDifficultyDesc = (difficulty) => {
  const config = difficultyConfig.value[difficulty]
  return `${config?.count || 5} ${t('games.riddles.questions')}`
}

// 生命周期
onMounted(() => {
  // 初始化时显示难度选择
})

onBeforeUnmount(() => {
  if (timerInterval) clearInterval(timerInterval)
})
</script>

<style scoped>
.riddles-game {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px);
  padding: 24px;
  background-color: var(--color-bg-light, #FAFBFC);
  gap: 20px;
}

/* 头部 */
.riddles-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 16px 20px;
  background: var(--color-bg-card);
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
  box-shadow: var(--shadow-sm, 0 1px 2px rgba(0,0,0,0.05));
}

.header-left h2 {
  margin: 0 0 4px 0;
  font-size: 24px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-label {
  margin: 0;
  font-size: 14px;
  color: var(--color-text-secondary, #6B7280);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 20px;
}

.game-stats {
  display: flex;
  gap: 20px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-label {
  font-size: 12px;
  color: var(--color-text-secondary, #6B7280);
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: bold;
  color: var(--color-primary-orange, #FF6B35);
}

.btn-exit {
  padding: 8px 16px;
}

/* 主内容区 */
.riddles-main {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 难度选择 */
.difficulty-selection {
  text-align: center;
}

.difficulty-selection h3 {
  margin-top: 0;
  margin-bottom: 32px;
  font-size: 28px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  max-width: 700px;
}

.difficulty-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 24px 20px;
  background: var(--color-bg-card);
  border: 2px solid var(--color-border, #E5E7EB);
  border-radius: 12px;
  cursor: pointer;
  transition: all var(--transition-base, 0.3s) var(--ease-in-out, ease-in-out);
  font-family: inherit;
}

.difficulty-btn:hover {
  border-color: var(--color-primary-orange, #FF6B35);
  background: rgba(255, 107, 53, 0.05);
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1));
}

.difficulty-name {
  font-weight: 600;
  font-size: 18px;
  color: var(--color-text-primary, #1F2937);
}

.difficulty-desc {
  font-size: 14px;
  color: var(--color-text-secondary, #6B7280);
}

/* 游戏内容 */
.game-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  gap: 20px;
}

.progress-section {
  background: var(--color-bg-card);
  padding: 16px 20px;
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
}

.progress-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.question-counter {
  font-weight: 600;
  color: var(--color-primary-orange, #FF6B35);
  min-width: 40px;
}

.progress-bar-container {
  flex: 1;
}

.progress-bar {
  height: 8px;
  background: var(--color-bg-light, #F9FAFB);
  border-radius: 999px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-primary-orange, #FF6B35);
  transition: width 0.3s ease;
}

.question-section {
  background: var(--color-bg-card);
  padding: 24px;
  border-radius: 12px;
  border: 1px solid var(--color-border, #E5E7EB);
  box-shadow: var(--shadow-md, 0 4px 6px rgba(0,0,0,0.1));
}

.question-card {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.question-text {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary, #1F2937);
  line-height: 1.6;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.option-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: var(--color-bg-card);
  border: 2px solid var(--color-border, #E5E7EB);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  font-family: inherit;
}

.option-btn:hover:not(:disabled) {
  border-color: var(--color-primary-orange, #FF6B35);
  background: rgba(255, 107, 53, 0.05);
}

.option-btn:disabled {
  cursor: not-allowed;
}

.option-selected:not(.option-correct):not(.option-incorrect) {
  border-color: var(--color-primary-orange, #FF6B35);
  background: rgba(255, 107, 53, 0.1);
}

.option-correct {
  border-color: var(--color-success, #10B981);
  background: #ECFDF5;
  color: #065F46;
}

.option-incorrect {
  border-color: var(--color-danger, #EF4444);
  background: #FEE2E2;
  color: #991B1B;
}

.option-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--color-bg-light, #F9FAFB);
  border-radius: 6px;
  font-weight: 700;
  min-width: 32px;
  color: var(--color-primary-orange, #FF6B35);
}

.option-text {
  flex: 1;
  font-size: 16px;
  font-weight: 500;
}

.explanation-section {
  margin-top: 20px;
}

.explanation-card {
  padding: 16px;
  border-radius: 10px;
  border-left: 4px solid;
}

.explanation-card.correct {
  background: #ECFDF5;
  border-left-color: var(--color-success, #10B981);
  color: #065F46;
}

.explanation-card.incorrect {
  background: #FEE2E2;
  border-left-color: var(--color-danger, #EF4444);
  color: #991B1B;
}

.explanation-card h4 {
  margin: 0 0 8px 0;
  font-weight: 600;
}

.explanation-card p {
  margin: 0 0 12px 0;
  line-height: 1.6;
}

.btn-next {
  background: var(--color-primary-orange, #FF6B35);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-next:hover {
  background: #FF5B1F;
}

/* 游戏结果 */
.game-result {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.result-card {
  background: var(--color-bg-card);
  padding: 40px;
  border-radius: 16px;
  border: 2px solid var(--color-primary-orange, #FF6B35);
  box-shadow: var(--shadow-lg, 0 10px 15px rgba(0,0,0,0.1));
  text-align: center;
  max-width: 500px;
}

.result-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.result-title {
  margin: 0 0 20px 0;
  font-size: 28px;
  color: var(--color-text-primary, #1F2937);
}

.result-stats {
  margin: 20px 0;
  padding: 20px;
  background: var(--color-bg-light, #F9FAFB);
  border-radius: 8px;
  text-align: left;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 12px 0;
  color: var(--color-text-primary, #1F2937);
}

.stat-row .stat-label {
  margin: 0;
}

.stat-row .stat-value {
  font-weight: 700;
  color: var(--color-primary-orange, #FF6B35);
}

.result-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 20px;
}

.btn-primary {
  background: var(--color-primary-orange, #FF6B35);
  color: white;
  border: none;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background: #FF5B1F;
}

.btn-secondary {
  background: var(--color-bg-card);
  border: 1px solid var(--color-border, #E5E7EB);
  color: var(--color-text-primary, #344054);
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-secondary:hover {
  border-color: var(--color-primary-orange, #FF6B35);
  color: var(--color-primary-orange, #FF6B35);
}

/* 响应式 */
@media (max-width: 768px) {
  .riddles-game {
    padding: 12px;
    gap: 12px;
  }

  .riddles-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-right {
    width: 100%;
    flex-direction: column;
  }

  .game-stats {
    width: 100%;
    justify-content: space-between;
  }

  .difficulty-options {
    grid-template-columns: 1fr;
  }

  .question-section {
    padding: 16px;
  }

  .result-actions {
    grid-template-columns: 1fr;
  }
}
</style>
